import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api.ts";
import type { PrintResult, SaleDetailOut } from "@/types/api.ts";

import { cashSessionKeys } from "./cashSessions.ts";

export const saleKeys = {
  all: ["sales"] as const,
  detail: (id: string) => [...saleKeys.all, "detail", id] as const,
};

/** Venta con boleta/factura cuyo comprobante todavía no tiene respuesta de SUNAT. */
export function isAwaitingSunat(sale: SaleDetailOut | undefined): boolean {
  if (!sale || sale.status === "draft") return false;
  if (sale.document_type !== "boleta" && sale.document_type !== "factura") return false;
  const doc = sale.electronic_document;
  return !doc || doc.status === "pending" || doc.void_status === "pending";
}

const SUNAT_POLL_MS = 4_000;
// ~3 min: después se deja de consultar; el comprobante sigue su curso en el
// cloud y se ve en Administración → Comprobantes (p. ej. sucursal sin internet).
const SUNAT_POLL_MAX_FETCHES = 45;

export function useSale(saleId: string | null | undefined) {
  return useQuery({
    queryKey: saleKeys.detail(saleId ?? ""),
    queryFn: () => api.get<SaleDetailOut>(`/sales/${saleId}`),
    enabled: !!saleId,
    // El estado SUNAT llega después de emitir (lo genera el cloud): se consulta
    // hasta tener respuesta para imprimir el QR.
    refetchInterval: (query) =>
      isAwaitingSunat(query.state.data) && query.state.dataUpdateCount < SUNAT_POLL_MAX_FETCHES
        ? SUNAT_POLL_MS
        : false,
  });
}

interface VoidSaleVars {
  sale_id: string;
  reason: string;
}

export function useVoidSale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sale_id, reason }: VoidSaleVars) =>
      api.post<SaleDetailOut>(`/sales/${sale_id}/void`, { reason }),
    onSuccess: (data) => {
      queryClient.setQueryData(saleKeys.detail(data.id), data);
      // Anular venta cash quita efectivo de la caja → refrescar indicador.
      queryClient.invalidateQueries({ queryKey: cashSessionKeys.current() });
    },
  });
}

interface PrintSaleVars {
  sale_id: string;
  /** Solo la primera impresión abre el cajón (si la caja lo tiene activo). */
  open_drawer: boolean;
}

/** Impresión directa ESC/POS en la ticketera de la caja (solo server local). */
export function usePrintSale() {
  return useMutation({
    mutationFn: ({ sale_id, open_drawer }: PrintSaleVars) =>
      api.post<PrintResult>(`/sales/${sale_id}/print`, undefined, {
        searchParams: { open_drawer },
      }),
  });
}
